import {
  CalendarRange,
  MapPin,
  SaudiRiyal,
  Stethoscope,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ListMyOffers } from '@cold-ambulance/client';
import { Skeleton, cn } from '@cold-ambulance/shadcn';
import {
  formatDate,
  getDocumentPhase,
  LocationLink,
  OfferActionBanner,
  sumEquipmentQuantities,
} from '@cold-ambulance/ui';

export function OfferCardSkeleton() {
  return (
    <div className="flex items-center justify-between rounded-lg border bg-white p-8">
      <div className="space-y-5">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded-sm" />
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Skeleton className="h-5 w-5 rounded-sm" />
          <Skeleton className="h-4 w-12" />
          <Skeleton className="size-2" />
          <Skeleton className="h-4 w-12" />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Skeleton className="h-5 w-5 rounded-sm" />
          <Skeleton className="h-4 w-12" />
          <span className="flex items-center gap-0.5 rounded-md border bg-white">
            <Skeleton className="h-3 w-8" />
          </span>
          <span className="flex items-center gap-0.5 rounded-md border bg-white">
            <Skeleton className="h-3 w-8" />
          </span>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 rounded bg-[#D5DCFF] p-2 pe-4">
        <div className="flex items-center gap-2 rounded bg-white px-4 py-2">
          <span className="flex items-center rounded-md bg-[#E9F6ED] px-1.5 py-0.5">
            <Skeleton className="h-4 w-10" />
          </span>
          <Skeleton className="h-5 w-16" />
          <div className="ms-2 flex items-center gap-2">
            <Skeleton className="h-6 w-6 rounded" />
            <Skeleton className="h-5 w-12" />
            <Skeleton className="h-5 w-20" />
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_MAP: Record<
  ListMyOffers['records'][number]['status'],
  { key: string; bgColor: string; textColor: string }
> = {
  PENDING: {
    key: 'offers.pending',
    bgColor: 'bg-[#FFF6F0]',
    textColor: 'text-[#EF7014]',
  },
  ACCEPTED: {
    key: 'offers.accepted',
    bgColor: 'bg-[#E9F6ED]',
    textColor: 'text-primary',
  },
  NOT_SELECTED: {
    key: 'offers.notSelected',
    bgColor: 'bg-[#F4F4F5]',
    textColor: 'text-[#71717A]',
  },
  CANCELED: {
    key: 'offers.canceled',
    bgColor: 'bg-[#F4F4F5]',
    textColor: 'text-[#71717A]',
  },
  IN_PROGRESS: {
    key: 'offers.inProgress',
    bgColor: 'bg-[#E9F6ED]',
    textColor: 'text-[#239D46]',
  },
};

export function OfferCard({
  offer,
}: {
  offer: ListMyOffers['records'][number];
}) {
  const { t } = useTranslation();
  const request = offer.request;

  const { phase, rejectionReason } = getDocumentPhase({
    offerId: request.offerId,
    requiredDocuments: request.requiredDocuments,
    acceptedOffer: offer,
  });

  return (
    <div className="rounded-lg border bg-white p-8">
      <div className="space-y-5">
        <div className="flex items-center gap-2">
          <MapPin size={20} />
          <p className="font-medium">{request.projectName}</p>
        </div>
        {request.location && (
          <LocationLink
            name={request.location.name}
            latitude={request.location.latitude}
            longitude={request.location.longitude}
            className="text-sm"
          />
        )}
        <div className="flex items-center gap-2 text-sm">
          <CalendarRange size={20} />
          {formatDate(request.scheduleStartDate)} -{' '}
          {formatDate(request.scheduleEndDate)}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Stethoscope size={20} />
          {request.ambulanceServiceWithDriver.reduce(
            (acc, service) =>
              acc +
              service.type1Amount +
              service.type2Amount +
              service.type3Amount,
            0,
          ) +
            request.ambulanceServiceWithoutDriver.reduce(
              (acc, service) =>
                acc +
                service.type1Amount +
                service.type2Amount +
                service.type3Amount,
              0,
            )}
          <span>{t('services.ambulance')}</span>
          {(() => {
            const equipmentCount =
              request.ambulanceServiceWithDriver.reduce(
                (acc, service) =>
                  acc +
                  sumEquipmentQuantities(service.type1Equipment) +
                  sumEquipmentQuantities(service.type2Equipment) +
                  sumEquipmentQuantities(service.type3Equipment),
                0,
              ) +
              request.ambulanceServiceWithoutDriver.reduce(
                (acc, service) =>
                  acc +
                  sumEquipmentQuantities(service.type1Equipment) +
                  sumEquipmentQuantities(service.type2Equipment) +
                  sumEquipmentQuantities(service.type3Equipment),
                0,
              ) +
              request.ambulance4x4WithDriver.reduce(
                (acc, service) => acc + sumEquipmentQuantities(service.equipment),
                0,
              ) +
              request.ambulance4x4WithoutDriver.reduce(
                (acc, service) => acc + sumEquipmentQuantities(service.equipment),
                0,
              ) +
              sumEquipmentQuantities(request.medicalEquipment);
            return equipmentCount > 0 ? (
              <span className="text-muted-foreground text-xs">
                ({equipmentCount} {t('services.equipment')})
              </span>
            ) : null;
          })()}
          <span className="flex items-center gap-0.5 rounded-md border bg-white px-1 py-px">
            {request.requestParamedicService.reduce(
              (acc, service) => acc + service.amount,
              0,
            )}
            <span>{t('services.paramedic')}</span>
          </span>
          <span className="flex items-center gap-0.5 rounded-md border bg-white px-1 py-px">
            {request.requestNurseService.reduce(
              (acc, service) => acc + service.amount,
              0,
            )}
            <span>{t('services.nurse')}</span>
          </span>
        </div>
      </div>
      <div className="mt-4 flex max-w-full items-center justify-between gap-2 rounded bg-[#D5DCFF] p-2 pe-4">
        <div className="ms-2 flex w-full items-center gap-2">
          <span className="text-base text-black">{t('offers.offeredPrice')}</span>
          <span className="text-base font-bold text-black">{offer.price}</span>
          <SaudiRiyal size={24} />
        </div>
        <div className="flex w-full items-center justify-between gap-2 rounded bg-white px-4 py-2">
          <span className="text-base font-medium text-black">{t('offers.status')}</span>
          <span
            className={cn(
              'flex items-center rounded-md px-1.5 py-0.5 text-sm leading-[1.6] font-medium text-[#239D46]',
              STATUS_MAP[offer.status].bgColor,
              STATUS_MAP[offer.status].textColor,
            )}
          >
            {t(STATUS_MAP[offer.status].key)}
          </span>
        </div>
      </div>

      <OfferActionBanner
        phase={phase}
        offerId={offer.id}
        projectName={request.projectName}
        documentsUploaded={offer.documents.length}
        documentsTotal={request.requiredDocuments.length}
        allDocsUploaded={offer.documents.length >= request.requiredDocuments.length}
        rejectionReason={rejectionReason}
      />

      <p className="text-muted-foreground mt-4 flex items-center gap-x-2 text-sm font-medium">
        <span>{t('offers.submittedOn')}</span>
        <span className="mt-0.5">{formatDate(offer.createdAt)}</span>
      </p>
    </div>
  );
}
